import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon, { FileTextIcon } from '@design-systems/react-icons'
import {
  POPOVER_GAP_PX,
  POPOVER_HIDE_DELAY_MS,
  POPOVER_MAX_HEIGHT_PX,
  POPOVER_VIEWPORT_MARGIN_PX,
  POPOVER_WIDTH_PX,
} from '../config/documentRailConfig'

// Anchors the panel to the trigger in viewport coordinates. It is rendered
// into `body` rather than beside the trigger because the rail's type list is
// a scroll container, and anything drawn inside it would be clipped at the
// edge. `position: fixed` plus these coordinates is what escapes that.
const getAnchor = (trigger) => {
  const rect = trigger.getBoundingClientRect()

  // The rail hugs the right edge of the window, so the panel opens beside the
  // trigger to its left — where the width is. Opening above or below instead
  // runs it into the sticky page header or off the foot of the screen.
  const hasRoomOnLeft =
    rect.left >= POPOVER_WIDTH_PX + POPOVER_GAP_PX + POPOVER_VIEWPORT_MARGIN_PX
  const horizontal = hasRoomOnLeft
    ? { right: window.innerWidth - rect.left + POPOVER_GAP_PX }
    : { left: POPOVER_VIEWPORT_MARGIN_PX }

  // Top-aligned with the trigger, then pulled back inside the window when a
  // full-height panel would overshoot. Clamping against the capped height
  // rather than the rendered one keeps this to a single layout pass.
  const top = Math.max(
    POPOVER_VIEWPORT_MARGIN_PX,
    Math.min(
      rect.top,
      window.innerHeight - POPOVER_MAX_HEIGHT_PX - POPOVER_VIEWPORT_MARGIN_PX,
    ),
  )

  return { ...horizontal, top }
}

const FileListPopover = ({ documents = [], label }) => {
  const [anchor, setAnchor] = useState(null)
  const hideTimer = useRef(null)
  const triggerRef = useRef(null)

  const cancelHide = () => {
    clearTimeout(hideTimer.current)
    hideTimer.current = null
  }

  const show = useCallback(() => {
    cancelHide()
    if (triggerRef.current) setAnchor(getAnchor(triggerRef.current))
  }, [])

  // Delayed so the pointer can travel from the trigger onto the panel — which
  // is a sibling in `body`, not a child — without it closing on the way.
  const hide = useCallback(() => {
    cancelHide()
    hideTimer.current = setTimeout(() => setAnchor(null), POPOVER_HIDE_DELAY_MS)
  }, [])

  useEffect(() => cancelHide, [])

  return (
    <>
      <span
        aria-label={`Show all ${documents.length} files`}
        className="document-status-rail__files-more"
        onBlur={hide}
        onFocus={show}
        onMouseEnter={show}
        onMouseLeave={hide}
        ref={triggerRef}
        tabIndex={0}
      >
        {label}
      </span>
      {anchor &&
        createPortal(
          <div
            className="document-status-rail__files-popover"
            onMouseEnter={cancelHide}
            onMouseLeave={hide}
            style={anchor}
          >
            <strong>{documents.length} files</strong>
            <ul>
              {documents.map((document) => (
                <li key={document.id}>
                  <Icon size="sm" svg={FileTextIcon} />
                  <span>{document.fileName}</span>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </>
  )
}

export default FileListPopover
